import { useState, useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react"; // 팝업 헤더에서만 사용
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuthVersion } from "../../hooks/useAuthVersion";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { registerModalClose } from "../../utils/modalStack";

const STORAGE_KEY = "emergency_notice_confirmed_at";
// 마지막 활성 공지를 캐시 — reload 직후 onSnapshot 첫 결과 도착(~1초)까지 빈 화면 깜빡임 차단
const CACHE_KEY = "emergency_notice_cache";

interface CachedNotice {
  messages: string[];
  updated_at: string;
}

function readCache(): CachedNotice {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { messages: [], updated_at: "" };
    const parsed = JSON.parse(raw);
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      updated_at: typeof parsed.updated_at === "string" ? parsed.updated_at : "",
    };
  } catch {
    return { messages: [], updated_at: "" };
  }
}

export default function EmergencyNotice() {
  const initial = readCache();
  const [messages, setMessages] = useState<string[]>(initial.messages);
  const [updatedAt, setUpdatedAt] = useState(initial.updated_at);
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(popupRef, showPopup);
  const authVersion = useAuthVersion(); // 로그인/로그아웃 시 팝업 재평가용

  // Firestore 구독은 첫 페인트 후로 지연(초기 로딩 경합 제거). 표시는 캐시(localStorage)로 즉시.
  useEffect(() => {
    let unsub = () => {};
    const start = () => {
      unsub = onSnapshot(
        doc(db, "emergency_notices", "active"),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const msgs = data.messages || [];
            const ts = data.updated_at || "";
            setMessages(msgs);
            setUpdatedAt(ts);
            try {
              localStorage.setItem(CACHE_KEY, JSON.stringify({ messages: msgs, updated_at: ts }));
            } catch { /* quota exceeded */ }
          } else {
            setMessages([]);
            setUpdatedAt("");
            try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
          }
        },
        () => setMessages([])
      );
    };
    const ric = (window as any).requestIdleCallback;
    const id = ric ? ric(start, { timeout: 2000 }) : window.setTimeout(start, 1200);
    return () => {
      if (ric && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(id);
      else window.clearTimeout(id);
      unsub();
    };
  }, []);

  // 팝업 트리거 — 공지(messages/updatedAt) 또는 로그인 상태(authVersion) 변화 시 재평가.
  // 로그인 사용자에게만 + 미확인(신규) 공지일 때만. (익명→로그인 전환 시에도 반영 → reload 없이 동작)
  useEffect(() => {
    if (messages.length > 0 && updatedAt && localStorage.getItem("access_token")) {
      const lastConfirmed = localStorage.getItem(STORAGE_KEY) || "";
      if (lastConfirmed !== updatedAt) setShowPopup(true);
    }
  }, [messages, updatedAt, authVersion]);

  const handleConfirm = () => {
    localStorage.setItem(STORAGE_KEY, updatedAt);
    setShowPopup(false);
  };

  // 요구사항 #550: 안드로이드 뒤로가기로 비상공지 확인 모달 닫기(=확인 처리). ref 로 최신 참조.
  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;
  useEffect(() => {
    if (!showPopup) return;
    const u = registerModalClose(() => confirmRef.current());
    return () => u();
  }, [showPopup]);

  // 비상공지 바는 항상 표시 (messages 비어도 바 자체 유지 — top 정렬 기준)

  const fullText =
    messages.map((msg) => `◆\u00A0${msg}`).join("\u00A0\u00A0\u00A0\u00A0") +
    "\u00A0\u00A0\u00A0\u00A0";
  const duration = Math.max(fullText.length * 0.2, 15);

  return (
    <>
      {/* 상단 마퀴 바 — 좌단 아이콘 제거 */}
      <div className="bg-blue-600/50 py-2 text-white overflow-hidden">
        <div className="mx-auto flex max-w-[var(--content-width)] items-center gap-3 px-[10px]">
          <div className="relative flex-1 overflow-hidden marquee-container">
            <div
              className="inline-flex whitespace-nowrap marquee-track"
              style={{ animationDuration: `${duration}s` }}
            >
              <span className="text-sm font-semibold">{fullText}</span>
              <span className="text-sm font-semibold">{fullText}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 신규 공지 팝업 */}
      {showPopup && (
        <div className="fixed inset-0 z-[9997] flex items-center justify-center" onKeyDown={(e) => { if (e.key === "Enter") handleConfirm(); }} tabIndex={0} ref={(el) => { (popupRef as React.MutableRefObject<HTMLDivElement | null>).current = el; el?.focus(); }}>
          <div className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-slate-900 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <h2 className="text-base font-bold text-white">TRIPPANG</h2>
            </div>
            <div className="space-y-2">
              {messages.map((msg, i) => (
                <div key={i} className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3">
                  <p className="text-sm text-white">{msg}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-center pt-2">
              <button
                onClick={handleConfirm}
                className="rounded-lg bg-amber-500 px-8 py-2.5 text-sm font-bold text-black hover:bg-amber-400 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
